import { motion } from "framer-motion";
import { Send, CheckCircle2, FileText, Download } from "lucide-react";
import { REPASSE_STATUS, formatBRL } from "@/data/financeData";

const StatusPill = ({ status }) => {
  const s = REPASSE_STATUS[status];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border ${s.bg} ${s.text} ${s.border} px-2.5 py-1 text-xs font-semibold`}>
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
      {s.label}
    </span>
  );
};

export const RepassesTable = ({ repasses, onRelease, onReceipt, onExport }) => {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200/70 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between border-b border-slate-100 px-6 py-4">
        <div>
          <h2 className="font-heading text-base font-bold text-slate-900">
            Gestão de Repasses
          </h2>
          <p className="text-xs text-slate-500">Controle de pagamentos a médicos colaboradores</p>
        </div>
        <button
          data-testid="export-extrato-btn"
          onClick={onExport}
          className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 shadow-sm transition-colors duration-200 hover:bg-slate-50"
        >
          <Download className="h-3.5 w-3.5 text-blue-600" />
          Exportar Extrato
        </button>
      </div>

      {/* Header */}
      <div className="hidden grid-cols-[1.6fr_1fr_1.2fr_0.8fr_1.2fr_1.4fr] gap-4 border-b border-slate-100 bg-slate-50/60 px-6 py-3 lg:grid">
        {["Médico", "Atendimentos", "Produção Bruta", "Taxa", "Líquido a Pagar", "Ação"].map((h, i) => (
          <span
            key={h}
            className={`text-[11px] font-semibold uppercase tracking-wide text-slate-400 ${
              i === 5 ? "text-right" : ""
            }`}
          >
            {h}
          </span>
        ))}
      </div>

      <div className="divide-y divide-slate-100">
        {repasses.map((r, i) => (
          <motion.div
            key={r.id}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: i * 0.05 }}
            data-testid={`repasse-row-${r.id}`}
            className="grid grid-cols-1 gap-4 px-6 py-4 transition-colors duration-200 hover:bg-slate-50/80 lg:grid-cols-[1.6fr_1fr_1.2fr_0.8fr_1.2fr_1.4fr] lg:items-center"
          >
            {/* Médico */}
            <div className="flex items-center gap-3">
              <img src={r.avatar} alt={r.doctor} className="h-9 w-9 rounded-full object-cover ring-1 ring-slate-200" />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-slate-900">{r.doctor}</p>
                <p className="truncate text-xs text-slate-400">{r.specialty}</p>
              </div>
            </div>

            {/* Atendimentos */}
            <div>
              <span className="lg:hidden text-xs text-slate-400">Atendimentos: </span>
              <span className="font-mono-num text-sm font-semibold text-slate-700">{r.atendimentos}</span>
            </div>

            {/* Produção */}
            <div>
              <span className="lg:hidden text-xs text-slate-400">Produção: </span>
              <span className="font-mono-num text-sm font-semibold text-slate-700">{formatBRL(r.producaoBruta)}</span>
            </div>

            {/* Taxa */}
            <div>
              <span className="lg:hidden text-xs text-slate-400">Taxa: </span>
              <span className="font-mono-num text-sm font-semibold text-slate-700">{r.taxa}%</span>
            </div>

            {/* Líquido */}
            <div>
              <span className="lg:hidden text-xs text-slate-400">Líquido: </span>
              <span className="font-mono-num text-base font-bold text-emerald-600">{formatBRL(r.valorLiquido)}</span>
            </div>

            {/* Ação */}
            <div className="flex items-center justify-start lg:justify-end">
              {r.status === "pago" ? (
                <div className="flex flex-col items-start gap-1.5 lg:items-end">
                  <span
                    data-testid={`repasse-paid-${r.id}`}
                    className="flex items-center gap-1.5 rounded-lg border border-emerald-200/70 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Pago via Pix
                  </span>
                  <button
                    data-testid={`receipt-repasse-${r.id}`}
                    onClick={() => onReceipt(r)}
                    className="flex items-center gap-1.5 text-xs font-semibold text-blue-600 transition-colors hover:text-blue-700"
                  >
                    <FileText className="h-3.5 w-3.5" />
                    Baixar recibo PDF
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-start gap-1.5 lg:items-end">
                  <button
                    data-testid={`release-repasse-${r.id}`}
                    onClick={() => onRelease(r.id)}
                    disabled={r.status === "processando"}
                    className="flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white shadow-[0_2px_8px_rgba(37,99,235,0.3)] transition-colors duration-200 hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
                  >
                    <Send className="h-3.5 w-3.5" />
                    Liberar via Pix / Extrato
                  </button>
                  <StatusPill status={r.status} />
                </div>
              )}
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};
